import { inject, Injectable, signal } from '@angular/core';
import html2canvas from 'html2canvas';
import { jsPDF as JsPDF } from 'jspdf';
import { SECONDS } from 'src/app/shared/functions/time';
import { timePromise } from 'src/app/shared/functions/time-promise';

import { ChartsService } from './bar-charts-service';
import { DownloadChartsPdfDialogService } from './download-charts-pdf-dialog.service';
import { DownloadChartsPdfSnackbarService } from './download-charts-pdf-snackbar.service';

const REPORT_NAME = 'Ticket-Insights-Report';

@Injectable()
export class DownloadChartsPdfService {
  private readonly snackbarService = inject(DownloadChartsPdfSnackbarService);

  private readonly dialogService = inject(DownloadChartsPdfDialogService);

  private readonly isDownloadingSignal$ = signal(false);

  readonly isDownloading = this.isDownloadingSignal$.asReadonly();

  async downloadCharts(charts: ChartsService): Promise<void> {
    if (this.isDownloadingSignal$()) {
      return;
    }

    let overlays: ReturnType<typeof this.openOverlays> | undefined;
    this.isDownloadingSignal$.set(true);

    try {
      overlays = this.openOverlays(charts);

      await timePromise(SECONDS(0.5));
      overlays.snackbarDetails = 'Generating PDF...';

      await timePromise(SECONDS(1.5));
      await this.captureHtmlToPdf(overlays.reportContainerElement);
      this.isDownloadingSignal$.set(false);
      overlays.dispose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating PDF:', error);
      this.isDownloadingSignal$.set(false);
      overlays?.dispose();
    }
  }

  async captureHtmlToPdf(element: HTMLElement | null): Promise<void> {
    if (element === null) {
      return;
    }

    this.cleanElementDom(element);
    await this.rasterizeSvgElements(element);

    const contentWidth = element.offsetWidth;
    const margin = 40;
    const pageWidth = contentWidth + margin * 2;

    const canvas = await html2canvas(element, {
      backgroundColor: '#F8F8F8',
      useCORS: true,
    });

    const contentHeight = (canvas.height / canvas.width) * contentWidth;
    const pageHeight = contentHeight + margin * 2;
    const canvasDataURL = canvas.toDataURL('image/png');

    const pdf = new JsPDF({
      format: [pageWidth, pageHeight],
      orientation: 'portrait',
      unit: 'px',
    });

    pdf.addImage(
      canvasDataURL,
      'PNG',
      margin,
      margin,
      contentWidth,
      contentHeight,
    );
    pdf.save(`${REPORT_NAME}.pdf`);
  }

  /**
   * Open the overlays for the snackbar and the dialog
   * @important This overlays should NOT Block Navigation
   * @param chartsService - The charts to download
   * @returns An object with the dispose function and the snackbar message setter
   */
  private openOverlays(chartsService: ChartsService) {
    const snackbarApi = this.snackbarService.createSnackbar({
      message: 'Preparing Charts...',
    });
    const dialogApi = this.dialogService.openDialog({
      chartsService,
    });

    return {
      dispose() {
        snackbarApi.dispose();
        dialogApi.dispose();
      },
      get reportContainerElement() {
        return dialogApi.htmlElement;
      },
      set snackbarDetails(value: string) {
        snackbarApi.details = value;
      },
    };
  }

  /**
   * Clean the DOM of the element to avoid the shadow and the header
   * for the PDF generation to be cleaner
   * @param element - The element to clean
   */
  private cleanElementDom(element: HTMLElement) {
    const statsElements = Array.from(
      element.querySelectorAll<HTMLElement>('.stats'),
    );

    statsElements.forEach((el) => {
      el.style.boxShadow = 'none';
      el.querySelector(':scope > :nth-child(3)')?.remove();
    });

    const headerElements = Array.from(
      element.querySelectorAll<HTMLElement>('div > div > h1'),
    );

    headerElements.forEach((el) => {
      el.parentElement.style.justifyContent = 'center';
      el.parentElement.style.border = 'none';

      el.parentElement
        .querySelectorAll(':scope > div')
        .forEach((x: HTMLElement) => {
          x.remove();
        });
    });
  }

  /**
   * html2canvas cannot reliably render SVGs with CSS/SVG transforms (e.g. rotated
   * gauge labels from ngx-gauge). Pre-rasterizing each SVG into a <canvas> element
   * sidesteps the issue entirely.
   */
  private async rasterizeSvgElements(container: HTMLElement): Promise<void> {
    const svgs = Array.from(container.querySelectorAll('svg'));

    await Promise.all(
      svgs.map(async (svg) => {
        const { width, height } = this.getSvgDimensions(svg);
        const clonedSvg = svg.cloneNode(true) as SVGSVGElement;

        clonedSvg.setAttribute('width', String(width));
        clonedSvg.setAttribute('height', String(height));
        if (!clonedSvg.getAttribute('xmlns')) {
          clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        }

        this.inlineComputedStyles(svg, clonedSvg);

        const svgString = new XMLSerializer().serializeToString(clonedSvg);
        const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;

        const scale = 2;
        const img = await this.loadImage(dataUrl);

        const canvas = document.createElement('canvas');
        canvas.width = width * scale;
        canvas.height = height * scale;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        const ctx = canvas.getContext('2d');
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, width, height);

        svg.parentNode?.replaceChild(canvas, svg);
      }),
    );
  }

  private getSvgDimensions(svg: SVGSVGElement): {
    width: number;
    height: number;
  } {
    const rect = svg.getBoundingClientRect();
    const width =
      rect.width ||
      parseFloat(svg.getAttribute('width') ?? '0') ||
      svg.clientWidth ||
      200;
    const height =
      rect.height ||
      parseFloat(svg.getAttribute('height') ?? '0') ||
      svg.clientHeight ||
      200;
    return { width, height };
  }

  /**
   * Recursively copy every computed style into the cloned element so the
   * serialized SVG string is fully self-contained.
   */
  private inlineComputedStyles(source: Element, target: Element): void {
    const computed = window.getComputedStyle(source);
    const targetEl = target as SVGElement & ElementCSSInlineStyle;

    for (let i = 0; i < computed.length; i++) {
      const prop = computed[i];
      targetEl.style.setProperty(prop, computed.getPropertyValue(prop));
    }

    const sourceChildren = Array.from(source.children);
    const targetChildren = Array.from(target.children);

    for (let i = 0; i < sourceChildren.length; i++) {
      if (targetChildren[i]) {
        this.inlineComputedStyles(sourceChildren[i], targetChildren[i]);
      }
    }
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }
}
